import { forwardRef, type ComponentPropsWithoutRef, type ReactElement } from 'react'
import { Virtuoso } from 'react-virtuoso'
import { ArrowUp } from 'lucide-react'
import { ListPagerFooter } from '../../../components/ui'
import type { InventoryProduct } from './inventoryTypes'
import { inventoryCopy } from './inventoryCopy'
import { LIST_PAGE_SIZE_OPTIONS } from '../../../constants/pagination'
import { InventoryProductsSkeleton } from './InventoryProductsSkeleton'
import styles from './Inventory.module.css'

const ic = inventoryCopy

// Virtuoso's `components` must keep their identity across renders. They used to be declared
// inline, so every render of this list — each keystroke, each selection toggle, the back-to-top
// button appearing — gave Virtuoso a NEW List component type, and React unmounted and rebuilt every
// visible row (fresh <img> elements, image decode, layout) to swap it in.
const ComfortableList = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(function ComfortableList(props, ref) {
  return <div {...props} ref={ref} className={styles.productsList} />
})
const CompactList = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(function CompactList(props, ref) {
  return <div {...props} ref={ref} className={`${styles.productsList} ${styles.productsListCompact}`} />
})
const COMFORTABLE_COMPONENTS = { List: ComfortableList }
const COMPACT_COMPONENTS = { List: CompactList }
const OVERSCAN = { main: 240, reverse: 240 }
const productKey = (_index: number, product: InventoryProduct) => product.id

export type InventoryProductsListProps = {
  density: 'comfortable' | 'compact'
  /** Table mode (Phase C2): rendered in place of the card list; skeleton, empty state and pager stay. */
  table?: ReactElement | null
  /** "Edit all N matching…" in the select-all bar (Phase C5); absent = not offered. */
  onEditAllMatching?: () => void
  listSoftRefetching: boolean
  productListInitialLoading: boolean
  skeletonCount: number
  products: InventoryProduct[]
  locale: string
  onEmptyActionNavigate: () => void
  /** Replaces "no products in inventory" when the list is empty because of what it is asking. */
  emptyMessage?: { title: string; body: string } | null
  selectedIds: Set<string>
  onSelectAllThisPage: () => void
  showSelectAllHintBar: boolean
  renderProductCard: (product: InventoryProduct, index: number) => ReactElement
  showInventoryBottomPager: boolean
  inventoryTotalCount: number
  inventoryPage: number
  inventoryTotalPages: number
  inventoryPageStart: number
  inventoryPageEnd: number
  limit: number
  offset: number
  hasMore: boolean
  onPrevPage: () => void
  onNextPage: () => void
  onPageSizeChange: (next: number) => void
  onPageSelect: (page: number) => void
  showInventoryScrollTopButton: boolean
  onScrollToTop: () => void
}

export function InventoryProductsList({
  density,
  table,
  onEditAllMatching,
  listSoftRefetching,
  productListInitialLoading,
  skeletonCount,
  products,
  locale,
  onEmptyActionNavigate,
  emptyMessage,
  selectedIds,
  onSelectAllThisPage,
  showSelectAllHintBar,
  renderProductCard,
  showInventoryBottomPager,
  inventoryTotalCount,
  inventoryPage,
  inventoryTotalPages,
  inventoryPageStart,
  inventoryPageEnd,
  limit,
  offset,
  hasMore,
  onPrevPage,
  onNextPage,
  onPageSizeChange,
  onPageSelect,
  showInventoryScrollTopButton,
  onScrollToTop,
}: InventoryProductsListProps) {
  return (
    <>
      {listSoftRefetching && <div className={styles.inventoryRefetchBar} aria-hidden="true" />}
      {productListInitialLoading ? (
        <InventoryProductsSkeleton count={skeletonCount} />
      ) : products.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📦</div>
          {emptyMessage ? (
            <>
              <h2>{emptyMessage.title}</h2>
              <p>{emptyMessage.body}</p>
            </>
          ) : (
            <>
              <h2>No products in inventory</h2>
              <p>Start adding cards to your inventory using Search & Browse</p>
              <div className={styles.emptyActions}>
                <button type="button" onClick={onEmptyActionNavigate} className={styles.btnPrimary}>
                  Search & Browse
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          {showSelectAllHintBar && (
            <div className={styles.selectAllBar}>
              <label className={styles.selectAllLabel}>
                <input
                  type="checkbox"
                  checked={products.length > 0 && products.every((p: InventoryProduct) => selectedIds.has(p.id))}
                  onChange={onSelectAllThisPage}
                  className={styles.selectCheckbox}
                />
                <span>{ic.selection.selectThisPage(products.length.toLocaleString(locale))}</span>
              </label>
              {onEditAllMatching ? (
                <button type="button" className={styles.editAllMatching} onClick={onEditAllMatching}>
                  Edit all {inventoryTotalCount.toLocaleString(locale)} matching…
                </button>
              ) : null}
            </div>
          )}

          {table ?? (
            <Virtuoso
              useWindowScroll
              data={products}
              style={{ width: '100%' }}
              computeItemKey={productKey}
              defaultItemHeight={density === 'compact' ? 132 : 640}
              overscan={OVERSCAN}
              components={density === 'compact' ? COMPACT_COMPONENTS : COMFORTABLE_COMPONENTS}
              itemContent={(index, product) => renderProductCard(product, index)}
            />
          )}

          <ListPagerFooter
            visible={showInventoryBottomPager}
            maxWidth={1600}
            info={`${inventoryTotalCount.toLocaleString(locale)} total products • Page ${inventoryPage} of ${inventoryTotalPages} • Showing ${inventoryPageStart.toLocaleString(locale)}-${inventoryPageEnd.toLocaleString(locale)} • ${limit} per page`}
            canPrev={offset > 0}
            canNext={hasMore}
            onPrev={onPrevPage}
            onNext={onNextPage}
            prevLabel="← Previous"
            nextLabel="Next →"
            pageSize={limit}
            pageSizeOptions={LIST_PAGE_SIZE_OPTIONS}
            onPageSizeChange={onPageSizeChange}
            currentPage={inventoryPage}
            totalPages={inventoryTotalPages}
            onPageSelect={onPageSelect}
            compactOnNarrowScreens
          />

          {showInventoryScrollTopButton && (
            <button
              type="button"
              className={styles.floatingScrollTopButton}
              onClick={onScrollToTop}
              aria-label="Back to top"
              title="Back to top"
            >
              <ArrowUp size={18} strokeWidth={2} aria-hidden />
            </button>
          )}
        </>
      )}
    </>
  )
}
